"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { NETWORK } from "@/lib/constant";
import type { NodeRuntime } from "@/lib/types";
import { buildSphere, buildSphereEdges, clamp, easeInOut, hashString } from "@/lib/utils/techNetwork.utils";
interface Options {
  techStack: string[];
  seed: string; // member slug — keeps the layout deterministic
}
const lerp = (a: number, b: number, r: number) => a + (b - a) * r;

/**
 * Owns the whole 3D tech-sphere. Nodes sit on a rotating shell; one
 * requestAnimationFrame loop rotates every point, projects it to 2D with
 * perspective, and writes straight to the DOM via refs — the mesh lines are
 * redrawn from the live projected positions, and the whole sphere springs
 * larger while the cursor is over the stage. React never re-renders per frame.
 */
export const useTechNetwork = t0 => {
  const $ = _c(21);
  const {
    techStack,
    seed
  } = t0;
  let t1;
  if ($[0] !== seed || $[1] !== techStack) {
    t1 = buildSphere(techStack, hashString(seed));
    $[0] = seed;
    $[1] = techStack;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const blueprints = t1;
  let t2;
  if ($[3] !== blueprints) {
    t2 = buildSphereEdges(blueprints);
    $[3] = blueprints;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const edges = t2;
  const stageRef = useRef(null);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const nodeEls = useRef(t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const lineEls = useRef(t4);
  const [size, setSize] = useState(0);
  const [reduced] = useState(_temp);
  let t5;
  if ($[7] !== blueprints) {
    t5 = blueprints.map(_temp2);
    $[7] = blueprints;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const runtime = t5;
  const sizeRef = useRef(0);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      x: 0,
      y: 0,
      inside: false
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const pointer = useRef(t6);
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      yaw: 0,
      pitch: 0,
      hover: 0
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const rot = useRef(t7);
  let t8;
  let t9;
  if ($[11] !== blueprints || $[12] !== edges || $[13] !== runtime) {
    t8 = () => {
      const stage = stageRef.current;
      if (!stage) {
        return;
      }
      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const ro = new ResizeObserver(() => {
        const s = Math.min(stage.clientWidth, stage.clientHeight);
        sizeRef.current = s;
        setSize(s);
      });
      ro.observe(stage);
      const onMove = e => {
        const r = stage.getBoundingClientRect();
        pointer.current.x = e.clientX - r.left;
        pointer.current.y = e.clientY - r.top;
        pointer.current.inside = true;
      };
      const onLeave = () => {
        pointer.current.inside = false;
      };
      stage.addEventListener("pointermove", onMove);
      stage.addEventListener("pointerleave", onLeave);
      const drawEdges = () => {
        for (let e_0 = 0; e_0 < edges.length; e_0++) {
          const line = lineEls.current[e_0];
          if (!line) {
            continue;
          }
          const a = runtime[edges[e_0].a];
          const b = runtime[edges[e_0].b];
          line.setAttribute("x1", `${a.x}`);
          line.setAttribute("y1", `${a.y}`);
          line.setAttribute("x2", `${b.x}`);
          line.setAttribute("y2", `${b.y}`);
          const depth = (a.z + b.z) * 0.5;
          const op = (0.12 + 0.42 * ((depth + 1) * 0.5)) * Math.min(a.appear, b.appear);
          line.setAttribute("opacity", `${op}`);
        }
      };
      const render = t => {
        const s_0 = sizeRef.current;
        if (!s_0) {
          return;
        }
        const cx = s_0 / 2;
        const cy = s_0 / 2;
        const avail = s_0 / 2 - NETWORK.edgeMargin;
        const p = pointer.current;
        const yawTarget = p.inside ? (p.x / s_0 - 0.5) * 2 * NETWORK.maxTilt : 0;
        const pitchTarget = p.inside ? (0.5 - p.y / s_0) * 2 * NETWORK.maxTilt : 0;
        rot.current.yaw = rot.current.yaw + (yawTarget - rot.current.yaw) * NETWORK.tiltSpring;
        rot.current.pitch = rot.current.pitch + (pitchTarget - rot.current.pitch) * NETWORK.tiltSpring;
        const hoverTarget = p.inside ? 1 : 0;
        rot.current.hover = rot.current.hover + (hoverTarget - rot.current.hover) * NETWORK.hoverSpring;
        const R = avail * NETWORK.radiusFill * lerp(1, NETWORK.hoverScale, rot.current.hover);
        const ry = t * NETWORK.spin + rot.current.yaw;
        const rx = Math.sin(t * 0.3) * NETWORK.wobble + rot.current.pitch;
        const cosY = Math.cos(ry);
        const sinY = Math.sin(ry);
        const cosX = Math.cos(rx);
        const sinX = Math.sin(rx);
        const persp = NETWORK.perspective;
        for (let i = 0; i < blueprints.length; i++) {
          const b_0 = blueprints[i];
          const rt = runtime[i];
          const rad = 1 + b_0.amp * (Math.sin(t * b_0.f1 + b_0.p1) + 0.5 * Math.sin(t * b_0.f2 + b_0.p2));
          const vx = b_0.bx * rad;
          const vy = b_0.by * rad;
          const vz = b_0.bz * rad;
          const x1 = vx * cosY + vz * sinY;
          const z1 = -vx * sinY + vz * cosY;
          const y2 = vy * cosX - z1 * sinX;
          const z2 = vy * sinX + z1 * cosX;
          const X = x1;
          const Y = y2;
          const Z = z2;
          const appear = easeInOut(clamp((t - b_0.delay) / NETWORK.entranceDur, 0, 1));
          const proj = persp / (persp - Z);
          const x = cx + X * R * proj * appear;
          const y = cy + Y * R * proj * appear;
          rt.x = x;
          rt.y = y;
          rt.z = Z;
          rt.appear = appear;
          const depthT = (Z + 1) * 0.5;
          let scale = lerp(NETWORK.depthScaleMin, NETWORK.depthScaleMax, depthT);
          let op_0 = lerp(NETWORK.depthOpacityMin, NETWORK.depthOpacityMax, depthT);
          if (b_0.important) {
            scale = scale * 1.1;
            op_0 = Math.max(op_0, 0.62);
          }
          scale = scale * appear;
          const opacity = op_0 * appear;
          const el = nodeEls.current[i];
          if (el) {
            el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${scale})`;
            el.style.opacity = `${opacity}`;
            el.style.zIndex = `${Math.round(depthT * 100)}`;
          }
        }
        drawEdges();
      };
      if (prefersReduced) {
        const id = requestAnimationFrame(() => render(1));
        return () => {
          cancelAnimationFrame(id);
          ro.disconnect();
          stage.removeEventListener("pointermove", onMove);
          stage.removeEventListener("pointerleave", onLeave);
        };
      }
      let raf = 0;
      let start = 0;
      const frame = now => {
        if (!start) {
          start = now;
        }
        raf = requestAnimationFrame(frame);
        render((now - start) / 1000);
      };
      raf = requestAnimationFrame(frame);
      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        stage.removeEventListener("pointermove", onMove);
        stage.removeEventListener("pointerleave", onLeave);
      };
    };
    t9 = [blueprints, edges, runtime];
    $[11] = blueprints;
    $[12] = edges;
    $[13] = runtime;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== blueprints || $[17] !== edges || $[18] !== reduced || $[19] !== size) {
    t10 = {
      blueprints,
      edges,
      stageRef,
      nodeEls,
      lineEls,
      size,
      reduced
    };
    $[16] = blueprints;
    $[17] = edges;
    $[18] = reduced;
    $[19] = size;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
function _temp() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function _temp2() {
  return {
    x: 0,
    y: 0,
    z: 0,
    appear: 0
  };
}